import { useEffect, useState, useCallback } from "react";
import logger, { error } from "../../../utils/logger";
import {
    setStandUps,
  setToastNotification,
  setTopOpportunities as setTopOpportunitiesRedux,
  setNews as setNewsRedux,
  clearNews,
  setLastFetchedLocation,
  setSuccessMessage,
  STANDUP_API_NAMES,
} from "../../../store/slices/standUpSlice";
import { apiClient, createApiClient, getLocalTimestamp, getTimezoneOffsetString } from "../../../api/apiClient";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../store";
import { useSelectedLocation } from "../../../hooks/useSelectedLocation";
import { TopOpportunityItem, TopOpportunitiesPagination } from "../components/TopOpportunitiesTable";
import { resetStandupRedirect, setSessionData } from "../../../store/slices/standupRedirectSlice";
import { handleStandUpErrorWithFriendlyMessage } from "../../../store/utils/standUpErrorHandler";
import { fetchCIDashboardData } from "../../../utils/dashboardUtils";
import useHomeScreenData from "../../homescreen/hooks/useHomeScreenData";
import { setCIDashboardData } from "../../../store/slices/homescreenSlice";

// Utility: format date string to "Jan 28 2026" for standup titles
// Parse UTC date string WITHOUT timezone conversion
export const formatDateForTitle = (dateString: string): string => {
  try {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    // Handle DD/MM/YYYY format
    if (dateString?.includes('/')) {
      const parts = dateString.split('/');
      if (parts.length === 3) {
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1; // 0-indexed
        const year = parseInt(parts[2], 10);
        const monthName = monthNames[month] ?? '';
        return `${monthName} ${day} ${year}`;
      }
    }

    // Parse ISO string directly without timezone conversion
    const isoMatch = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) {
      const year = isoMatch[1];
      const month = parseInt(isoMatch[2], 10);
      const day = parseInt(isoMatch[3], 10);
      const monthName = monthNames[month - 1];
      return `${monthName} ${day} ${year}`;
    }

    return dateString;
  } catch (e) {
    console.error('Error formatting date for title:', e);
    return dateString;
  }
};

// Helper function to transform standupType for API
const transformStandupType = (type: string): string => {
  const standupTypeMap: { [key: string]: string } = {
    'daily-lt': 'Daily_LT',
    'daily_lt': 'Daily_LT',
    'weekly-lt': 'Weekly_LT',
    'weekly_lt': 'Weekly_LT',
    'daily-frontline': 'Daily_Frontline',
    'daily_frontline': 'Daily_Frontline',
    'daily_flt': 'Daily_Frontline',
  };
  return standupTypeMap[type.toLowerCase()] || type;
};

// Helper function to transform standupType for API
const transformStandupTypeForFavourites = (type: string): string => {
  const standupTypeMap: { [key: string]: string } = {
    'daily-lt': 'daily_lt',
    'weekly-lt': 'weekly_lt',
    'daily-frontline': 'daily_flt',
  };
  return standupTypeMap[type.toLowerCase()] || type;
};

// Utility: format date string to "January 28, 02:40 PM" for modified dates
// Parse UTC date string WITHOUT timezone conversion
export const formatModifiedDate = (dateString?: string): string => {
  if (!dateString) {
    return "";
  }

  try {
    // Convert UTC timestamp to local timezone
    const date = new Date(dateString);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return dateString;
    }
    
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                        'July', 'August', 'September', 'October', 'November', 'December'];
    
    const month = monthNames[date.getMonth()];
    const day = date.getDate();
    let hour = date.getHours();
    const minute = date.getMinutes().toString().padStart(2, "0");
    
    const ampm = hour >= 12 ? "PM" : "AM";
    hour = hour % 12;
    hour = hour ? hour : 12;
    const hourStr = hour.toString().padStart(2, "0");
    
    return `${month} ${day}, ${hourStr}:${minute} ${ampm}`;
  } catch (e) {
    console.error('Error formatting date:', e);
    return dateString;
  }
};

export const useStandUpDashboard = () => {
  // Redux dispatch
  const dispatch = useDispatch();

  // Get data from Redux
  const standUpState = useSelector((state: RootState) => state.standUp);
  const selectedLocation  = useSelectedLocation();
  const userRoleId = useSelector(
    (state: RootState) =>
      state.appData?.userInfo?.roleId ?? state.auth?.user?.roleId ?? null
  );
  const userEmail = useSelector(
    (state: RootState) =>
      state.appData?.userInfo?.email ?? state.auth?.user?.email ?? ""
  );
  const userGpid = useSelector(
    (state: RootState) =>
      state.appData?.userInfo?.GPID ?? state.auth?.user?.gpid ?? ""
  );

  const standupId = useSelector((state: RootState) => state.standupRedirect.session.standupId);
 
  // Component state - use Redux as source of truth
  const [standupListItems, setStandupListItems] = useState(standUpState.standUps);
  const [newsList, setNewsList] = useState(standUpState.news);
  const clientTimestamp = getLocalTimestamp();
  const [isLoadingStandup, setIsLoadingStandup] = useState(false);
  const [topOpportunitiesTableData, setTopOpportunities] = useState<TopOpportunityItem[]>([]);
  const [inProgressItems, setInProgressItems] = useState<TopOpportunityItem[]>([]);
  const [overdueItems, setOverdueItems] = useState<TopOpportunityItem[]>([]);
  const [completedItems, setCompletedItems] = useState<TopOpportunityItem[]>([]);
  const [pagination, setPagination] = useState<TopOpportunitiesPagination | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoadingNews, setIsLoadingNews] = useState(false);

  // Navigation state
  const [showActionPlanDetails, setShowActionPlanDetails] = useState(false);
  const [selectedActionPlanId, setSelectedActionPlanId] = useState<string | null>(null);
  const [selectedActionPlanData, setSelectedActionPlanData] = useState<TopOpportunityItem | null>(null);
  const [isViewingActionPlanDetails, setIsViewingActionPlanDetails] = useState(false);
  const [isLoadingTopOpportunities, setIsLoadingTopOpportunities] = useState(false);
  const [isTogglingFavorite, setIsTogglingFavorite] = useState(false);

  // Memoize transform functions to ensure stable references
  const memoizedTransformStandupType = useCallback(transformStandupType, []);
  const memoizedTransformStandupTypeForFavourites = useCallback(transformStandupTypeForFavourites, []);

  // Sync local state with Redux
  useEffect(() => {
    setStandupListItems(standUpState.standUps);
  }, [standUpState.standUps]);

  useEffect(() => {
    setNewsList(standUpState.news);
  }, [standUpState.news]);

  useEffect(() => {
    // Update filtered items when top opportunities change
    const opportunities = standUpState.topOpportunities;
    const inProgressData = opportunities.filter((item: any) => item.STATUS === "in_progress");
    const completedData = opportunities.filter((item: any) => item.STATUS === "completed");
    const overdueData = opportunities.filter((item: any) => item.STATUS === "overdue");
    setInProgressItems(inProgressData);
    setCompletedItems(completedData);
    setOverdueItems(overdueData);
  }, [standUpState.topOpportunities]);


  // Fetch CI Dashboard data when location changes
  const fetchCIDashboard = async () => {
    try {
      const ciResponse = await fetchCIDashboardData(selectedLocation);
      logger.log("CI Dashboard Data fetched in standup:", ciResponse);
      // Dispatch CI dashboard data to Redux homescreen slice
      dispatch(setCIDashboardData(ciResponse));
    } catch (err: any) {
      console.error("Error fetching CI Dashboard:", err);
    }
  };

  // Fetch standup details from standup API on mount/location change
  const fetchStandUpData = async () => {
    setIsLoadingStandup(true);
    const roleIdToUse = String(userRoleId || "unknown");
    const clientTimezone = getTimezoneOffsetString();
    const apiUrl = `/stand-up?location_id=${encodeURIComponent(
      selectedLocation?.id
    )}&roleID=${encodeURIComponent(
      roleIdToUse
    )}&location_name=${encodeURIComponent(selectedLocation?.displayText)}&clientTimestamp=${encodeURIComponent(clientTimestamp)}&clientTimezone=${encodeURIComponent(clientTimezone)}&sort_by=modified_at&sort_order=DESC`;

    try {
      console.log("Fetching standup data");
      const api = createApiClient();
      const response = await apiClient(api, apiUrl, "GET");
      logger.log("Fetching standup data success");
      logger.log("Stand up response", response);

      if (response?.success === true && response?.data) {
        logger.log("Stand up data fetched:", response.data);
        
        // Format the modified dates in the data
        const formattedData = response.data.map((item: any) => ({
          ...item,
          modified_at: formatModifiedDate(item.modified_at)
        }));
        
        setStandupListItems(formattedData);
        dispatch(setStandUps(formattedData));
      }
    } catch (error: any) {
      console.error("Error fetching stand up data:", error);

      // If the server reports the resource was not found (likely deleted), suppress the error modal
      const statusCode = error?.response?.status;
      if (statusCode === 404) {
        console.log("No Standup");
        return;
      }

      // Use centralized error handler with modal display
      handleStandUpErrorWithFriendlyMessage(
        STANDUP_API_NAMES.FETCH_STANDUP_DATA,
        error,
        true, // Show error modal
        { locationId: selectedLocation?.id, roleId: userRoleId }
      );
    }
    finally {
      setIsLoadingStandup(false);
    }
  };
   // Fetch top Opportunities 
  const fetchTopOpportunitiesData = async (page: number = 1, pageSize: number = 10, sessionId?: string, isTemp?: string) => {
   setIsLoadingTopOpportunities(true);
   const sessionIdParam = sessionId ? `&session_id=${encodeURIComponent(sessionId)}` : '';
   // Always send is_temp=false for Top Opportunities regardless of sessionId
   const isTempParam = isTemp !== "no_need" ? `&is_temp=false` : '';
   const clientTimezone = getTimezoneOffsetString();
   const apiUrl = `/action-plans?source=All&type=All&priority=All&status=All&assignment_status=All&location=${encodeURIComponent(selectedLocation?.displayText)}&top_opportunity=1&pageNumber=1&pageSize=100&clientTimestamp=${encodeURIComponent(clientTimestamp)}&clientTimezone=${encodeURIComponent(clientTimezone)}${sessionIdParam}${isTempParam}`;

    try {
      console.log("Fetching standup data");
      const api = createApiClient();
      const response = await apiClient(api, apiUrl, "GET");
      logger.log("Fetching opportunities data success");
      logger.log("Stand up response", response);

      if (response?.status === "success" && response?.data) {
        logger.log("✅ Top opportunities data fetched:", response.data);
         // Ensure response.data is an array
        const opportunities = Array.isArray(response.data) ? response.data : [];
        setTopOpportunities(opportunities);
        setPagination(response.pagination || null);
        setCurrentPage(page);
        const inProgressData = opportunities.filter((item: any) => item.STATUS === "in_progress");
        const completedData = opportunities.filter((item: any) => item.STATUS === "completed");
        const overdueData = opportunities.filter((item: any) => item.STATUS === "overdue");
         setInProgressItems(inProgressData || []);
         setCompletedItems(completedData || []);
         setOverdueItems(overdueData || []);
        dispatch(setTopOpportunitiesRedux(opportunities));
        // dispatch(setStandUps(response.data));
      }
    } catch (error: any) {
      console.error("Error fetching top opportunities data:", error);

      // If the server reports the resource was not found (likely deleted), suppress the error toast
      const statusCode = error?.response?.status;
      if (statusCode === 404) {
        console.log("No top opportunities");
        return;
      }

      // Use centralized error handler with modal display
      handleStandUpErrorWithFriendlyMessage(
        STANDUP_API_NAMES.FETCH_TOP_OPPORTUNITIES,
        error,
        true, // Show error modal
        { locationName: selectedLocation?.displayText, page, pageSize }
      );
    } finally {
      setIsLoadingTopOpportunities(false);
    }
  };

  // Fetch news data
  const fetchNewsData = async () => {
    // Clear news data before fetching new data for new location
    dispatch(clearNews());
    setNewsList([]);
    
    // Get current client timestamp in local timezone and timezone offset
    const clientTimestamp = getLocalTimestamp();
    const clientTimezone = getTimezoneOffsetString(); // e.g., "UTC+5:30"
    
    // Send both clientTimestamp and clientTimezone as query parameters so backend can properly
    // extract UTC time from local timestamp using the timezone offset
    const apiUrl = `/stand-up/news?location=${encodeURIComponent(selectedLocation?.displayText)}&clientTimestamp=${encodeURIComponent(clientTimestamp)}&clientTimezone=${encodeURIComponent(clientTimezone)}`;

    setIsLoadingNews(true);
    try {
      console.log("Fetching news data with clientTimestamp:", clientTimestamp, "clientTimezone:", clientTimezone);
      const api = createApiClient();
      const response = await apiClient(api, apiUrl, "GET");
      logger.log("Fetching news data success");
      logger.log("News response", response);

      if (response?.success === true && response?.data) {
        logger.log("News data fetched:", response.data);
        // Format date helper with timezone conversion to show correct local date
        const formatNewsDate = (isoString: string): string => {
          try {
            // Convert UTC timestamp to local timezone
            const date = new Date(isoString);
            
            // Check if date is valid
            if (isNaN(date.getTime())) {
              return isoString;
            }
            
            const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                                'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            const year = date.getFullYear();
            const month = date.getMonth();
            const day = date.getDate();
            return `${day} ${monthNames[month]} ${year}`;
          } catch (e) {
            return isoString;
          }
        };

        // Map API response to component format
        const mappedNews = response.data.map((item: any) => ({
          id: item.id.toString(),
          title: item.title,
          description: item.description,
          is_active: item.is_active,
          is_favourite: item.is_favourite,
          date: formatNewsDate(item.created_at),
          createdBy: item.created_by,
          is_truncated: item.is_truncated,
        }));
        setNewsList(mappedNews);
        // Store in Redux
        dispatch(setNewsRedux(mappedNews));
      }
    } catch (error: any) {
      console.error("Error fetching news data:", error);

      const statusCode = error?.response?.status;
      if (statusCode === 404) {
        console.log("No news found");
        setNewsList([]);
        dispatch(setNewsRedux([]));
        return;
      }

      // Use centralized error handler with modal display
      handleStandUpErrorWithFriendlyMessage(
        STANDUP_API_NAMES.FETCH_NEWS,
        error,
        true, // Show error modal
        { locationName: selectedLocation?.displayText }
      );
    } finally {
      setIsLoadingNews(false);
    }
  };
 const handleViewActionPlanDetails = (item: TopOpportunityItem) => {
    setSelectedActionPlanId(String(item.ID));
    setSelectedActionPlanData(item);
    setShowActionPlanDetails(true);
    setIsViewingActionPlanDetails(false);
    console.log('Navigating to ActionPlanDetails with ID:', item.ID, 'in view mode');
  };

  const handleOpenActionPlanDetailsById = (actionPlanId: string) => {
    setSelectedActionPlanId(String(actionPlanId));
    setSelectedActionPlanData(null);
    setShowActionPlanDetails(true);
    setIsViewingActionPlanDetails(false);
    console.log('Navigating to ActionPlanDetails with ID:', actionPlanId, 'via external navigation');
  };

  const handleViewActionPlan = (item: TopOpportunityItem) => {
    setSelectedActionPlanId(String(item.ID));
    setSelectedActionPlanData(item);
    setShowActionPlanDetails(true);
    setIsViewingActionPlanDetails(false);
    console.log('Navigating to ActionPlanDetails with ID:', item.ID, 'in view mode from add button');
  };

  const handleBackFromDetails = () => {
    setShowActionPlanDetails(false);
    setSelectedActionPlanId(null);
    setSelectedActionPlanData(null);
    setIsViewingActionPlanDetails(false);
    // Refetch top opportunities when navigating back to the dashboard
    try {
      fetchTopOpportunitiesData(currentPage, pagination?.pageSize || 10);
    } catch (e) {
      console.warn('Failed to refetch top opportunities on back navigation', e);
    }
  };

  const handlePageChange = (page: number) => {
    console.log('Page changed to:', page);
    setCurrentPage(page);
    fetchTopOpportunitiesData(page, pagination?.pageSize || 10);
  };

  const handlePageSizeChange = (pageSize: number) => {
    console.log('Page size changed to:', pageSize);
    setCurrentPage(1);
    fetchTopOpportunitiesData(1, pageSize);
  };

  /**
   * Submit standup creation
   * Handles the POST /stand-up API call with proper payload formatting
   */
  const submitStandUp = async (params: {
    standupId?: string; // Optional for creation, required for updates
    sessionData: {
      sessionId: string;
      standupTitle: string;
      standupType: string;
      standupDate: string;
    };
    periodWeekData: {
      period: string;
      week: string;
      year: string;
    };
    onSuccess?: () => void;
    onError?: (error: any) => void;
    standupStatus?: string;
    isSaveForLater?: boolean;
    isSavedForLater?: boolean;
    isSubmitted?: boolean;
  }) => {
    const {standupStatus, sessionData, periodWeekData, onSuccess, onError, isSaveForLater, isSavedForLater = false, isSubmitted = false } = params;

    try {
      // Map standup type short codes to full API-expected names
      const standupTypeMap: { [key: string]: string } = {
        'daily_lt': 'daily_lt',
        'daily-lt': 'daily_lt',
        'weekly_lt': 'weekly_lt',
        'weekly-lt': 'weekly_lt',
        'weekly': 'weekly_lt',
        'daily_flt': 'daily_flt',
        'daily-flt': 'daily_flt',
        'daily_frontline': 'daily_flt',
        'daily-frontline': 'daily_flt',
      };

      // Convert standup type to API-expected format
      const apiStandupType = standupTypeMap[sessionData.standupType.toLowerCase()] || sessionData.standupType;

      logger.log('Creating stand-up with type:', apiStandupType, 'and scheduled date:', sessionData.standupDate); 

      // sessionData.standupTitle is already formatted as "Daily Leadership Stand-up Jan 28 2026"
      // Prepare payload from Redux store
      const payload: any = {
        standup_id: standupId || "",
        standup_session_id: sessionData.sessionId,
        title: sessionData.standupTitle,
        description: sessionData.standupTitle, // Using title as description as per requirement
        standup_type: apiStandupType,
        scheduled_date: sessionData.standupDate,
        status: standupStatus,
        period: String(periodWeekData?.period || ""),
        week: String(periodWeekData?.week || ""),
        year: String(periodWeekData?.year || ""),
        location_id: selectedLocation?.id?.toString() || "",
        location_name: selectedLocation?.displayText || "",
      };
      
      // Add is_submitted field based on flags and standup status
      if (standupStatus === "In Progress") {
        if (isSubmitted) {
          payload.is_submitted = true;
        } else if (isSavedForLater) {
          payload.is_submitted = false;
        }
      }

      console.log('POST /stand-up payload:', payload);

      // Make API call
      const api = createApiClient();
      const clientTimezone = getTimezoneOffsetString();
      const response = await apiClient(api, `/stand-up?clientTimezone=${encodeURIComponent(clientTimezone)}`, 'POST', payload);

      console.log('Stand-up creation response:', response);

      // Show success toast including the standup title when available
      const savedTitle = sessionData?.standupTitle || payload.title || "";
      const savedMessage = savedTitle ? `The standup "${savedTitle}" has been saved` : "The standup has been saved";
      dispatch(setToastNotification({
        header: "",
        subtext: savedMessage,
        variant: "success",
        position: "top",
        duration: 4000,
      }));

      // Set success message for display on dashboard
      dispatch(setSuccessMessage(savedMessage));

      // Store standupId in Redux session data
      const returnedStandupId = response?.data?.id;
      if (returnedStandupId) {
        dispatch(setSessionData({ standupId: String(returnedStandupId) }));
        console.log('Standup ID stored in Redux:', returnedStandupId);
      }

      // Note: Standup data will be refreshed automatically when returning to dashboard
      // No need to fetch here as handleBackToStandUp calls fetchStandUpData()

      // // Reset the entire redirect slice after submission
      // dispatch(resetStandupRedirect());

      // Call success callback if provided
      if (onSuccess) {
        onSuccess();
      }

      return { success: true, data: response };
    } catch (error: any) {
      console.error('Error creating stand-up:', error);
      
      // Use centralized error handler to show overlay/modal
      handleStandUpErrorWithFriendlyMessage(
        STANDUP_API_NAMES.SUBMIT_STANDUP,
        error,
        true, // displayModal = true → show overlay
        { standupTitle: sessionData?.standupTitle, status: standupStatus }
      );

      // Call error callback if provided
      if (onError) {
        onError(error);
      }

      return { success: false, error };
    }
  };

  /**
   * Save standup for later
   * Handles the PUT /stand-up API call to save standup progress
   */
  const updateStandup = async (params: {
    standupId: string;
    sessionData: {
      standupTitle: string | undefined;
      sessionId: string;
      standupType: string;
    };
    isEditMode?: boolean;
    standupStatus?: string;
    onSuccess?: () => void;
    onError?: (error: any) => void;
    isSavedForLater?: boolean;
    isSubmitted?: boolean;
  }) => {
    const { standupId, sessionData, isEditMode, standupStatus, onSuccess, onError, isSavedForLater = false, isSubmitted = false } = params;

    try {
      logger.log('Save for later pressed - button clicked');
      // Map standup type short codes to full API-expected names
      const standupTypeMap: { [key: string]: string } = {
        'daily_lt': 'daily_lt',
        'daily-lt': 'daily_lt',
        'weekly_lt': 'weekly_lt',
        'weekly-lt': 'weekly_lt',
        'daily_flt': 'daily_flt',
        'daily-flt': 'daily_flt',
        'daily_frontline': 'daily_flt',
        'daily-frontline': 'daily_flt',
      };

      // Convert standup type to API-expected format
      const apiStandupType = standupTypeMap[sessionData.standupType.toLowerCase()] || sessionData.standupType;
      
      // Prepare payload for Save for Later
      // In edit mode: include both standup_id and standup_session_id
      // In new/view mode: include only standup_id
      const payload: any = isEditMode ? {
        standup_id: standupId,
        standup_session_id: sessionData.sessionId,
        location_id: selectedLocation?.id?.toString() || "",
        location_name: selectedLocation?.displayText || "",
        standup_type: apiStandupType,
        status: standupStatus,
      } : {
        standup_id: standupId,
        standup_session_id: sessionData.sessionId,
        location_id: selectedLocation?.id?.toString() || "",
        location_name: selectedLocation?.displayText || "",
        standup_type: apiStandupType,
        status: standupStatus,
      };
      
      // Add is_submitted field based on flags and standup status
      if (standupStatus === "In Progress") {
        if (isSubmitted) {
          payload.is_submitted = true;
        } else if (isSavedForLater) {
          payload.is_submitted = false;
        }
      }
       if (standupStatus === "Completed") {
         payload.is_submitted = true;  
      }
      
      logger.log('PUT /stand-up payload for Save for Later:', payload);
      
      // Make API call using PUT method
      const api = createApiClient();
      const clientTimezone = getTimezoneOffsetString();
      logger.log('Sending PUT request to /stand-up for Save for Later', api);
      const response = await apiClient(api, `/stand-up?clientTimestamp=${encodeURIComponent(clientTimestamp)}&clientTimezone=${encodeURIComponent(clientTimezone)}`, 'PUT', payload);
      logger.log('Stand-up save for later response:', response);
      
      // Show success toast
      dispatch(setToastNotification({
        header: "",
        subtext: standupStatus === "Completed" ? "The Stand-up has been completed successfully" : "The Stand-up has been updated",
        variant: "success",
        position: "top",
        duration: 4000,
      }));

      if (standupStatus === "Completed") {
        try {
          const matched = standupListItems.find(
            (s: any) =>
              String(s.id) === String(standupId) || s.id === Number(standupId),
          );
          const title = matched?.title || sessionData?.standupTitle || "";
          if (title) {
            dispatch(
              setSuccessMessage(
                `The standup '${title}' has been Completed`,
              ),
            );
          } else {
            dispatch(
              setSuccessMessage("The standup has been completed successfully"),
            );
          }
        } catch (e) {
          dispatch(
            setSuccessMessage("The standup has been completed successfully"),
          );
        }
      } else {
        dispatch(setSuccessMessage("Stand-up updated successfully"));
      }
      dispatch(setSuccessMessage("The Stand-up has been updated"));
      // Note: Standup data will be refreshed automatically when returning to dashboard
      // No need to fetch here as handleBackToStandUp calls fetchStandUpData()
      
      // dispatch(resetStandupRedirect());
      // Call success callback if provided
      if (onSuccess) {
        onSuccess();
      }

      return { success: true, data: response };
    } catch (error: any) {
      logger.error('Error saving stand-up:', error);
      
      // Use centralized error handler to show overlay/modal
      handleStandUpErrorWithFriendlyMessage(
        STANDUP_API_NAMES.UPDATE_STANDUP,
        error,
        true, // displayModal = true → show overlay
        { standupId, standupStatus, action: 'save_for_later' }
      );

      // Call error callback if provided
      if (onError) {
        onError(error);
      }

      return { success: false, error };
    }
  };

  // Handle toggling favorite/top opportunity status
  const handleToggleFavoriteOpportunity = async (id: string, sessionId?: string) => {
    setIsTogglingFavorite(true);
    try {
      // Find the current item to get its current TOP_OPPORTUNITY status
      const currentItem = standUpState.topOpportunities.find(
        (item: TopOpportunityItem) => String(item.ID) === id,
      );
      if (!currentItem) {
        console.error("Action plan not found for ID:", id);
        setIsTogglingFavorite(false);
        return;
      }

      // Determine the new TOP_OPPORTUNITY value (toggle current state)
      const currentTopOpportunity =
        currentItem.TOP_OPPORTUNITY === 1 ||
        currentItem.TOP_OPPORTUNITY === true;
      const newTopOpportunity = !currentTopOpportunity;

      // Check if this is a temp action plan
      const isTemp = currentItem.IS_TEMP === 1;

      // Create the API payload
      let putPayload: any = {
        top_opportunity: newTopOpportunity,
        updated_by: userGpid || userEmail || "system",
        updated_by_email: userEmail,
      };

      // Include is_temp and session_id when in standup mode
      if (sessionId) {
        putPayload.is_temp = isTemp;
        putPayload.session_id = sessionId;
      }

      // Only add clientTimestamp when is_temp is false or sessionId is not provided
      if (!isTemp || !sessionId) {
        putPayload.clientTimestamp = getLocalTimestamp();
      }

      // Include role id for RBAC validation if available
      if (userRoleId) {
        putPayload.updated_by_role_id = userRoleId;
      }

      console.log('========================================');
      console.log('TOGGLE TOP OPPORTUNITY - Request Details');
      console.log('========================================');
      console.log('Action Plan ID:', currentItem.ID);
      console.log('Current IS_TEMP:', currentItem.IS_TEMP);
      console.log('Session ID received:', sessionId);
      console.log('New top_opportunity value:', newTopOpportunity);
      console.log('PUT /action-plans payload:', JSON.stringify(putPayload, null, 2));
      console.log('========================================');

      // Call the PUT API to update the action plan
      const api = createApiClient();
      const clientTimezone = getTimezoneOffsetString();
      const putResponse = await apiClient(
        api,
        `/action-plans/${currentItem.ID}?clientTimezone=${encodeURIComponent(clientTimezone)}`,
        "PUT",
        putPayload,
      );

      console.log("Action Plan top opportunity update response:", putResponse);

      if (putResponse?.status === "success") {
        // Show success toast
        dispatch(
          setToastNotification({
            header: "",
            subtext: newTopOpportunity
              ? "Added to top opportunities"
              : "Removed from top opportunities",
            variant: "success",
            position: "top",
            duration: 4000,
          }),
        );

        // Refetch the top opportunities data to reflect the changes
        await fetchTopOpportunitiesData(
          currentPage,
          pagination?.pageSize || 10,
          sessionId,
          "no_need"
        );
      } else {
        throw new Error(
          putResponse?.message || "Failed to update top opportunity status",
        );
      }
    } catch (error: any) {
      console.error("Error toggling favorite opportunity:", error);

      // Use centralized error handler to show overlay/modal
      handleStandUpErrorWithFriendlyMessage(
        STANDUP_API_NAMES.TOGGLE_FAVORITE_OPPORTUNITY,
        error,
        true, // displayModal = true → show overlay
        { actionPlanId: id }
      );
    } finally {
      setIsTogglingFavorite(false);
    }
  };

  return {
    // State
    standupListItems,
    setStandupListItems, // Added this to expose the setter
    inProgressItems,
    completedItems,
    overdueItems,
    newsList,
    fetchTopOpportunitiesData,
    isLoadingStandup,
    isLoadingTopOpportunities,
    isLoadingNews,
    isTogglingFavorite,
    showActionPlanDetails,
    selectedActionPlanId,
    selectedActionPlanData,
    handleBackFromDetails,
    transformStandupType: memoizedTransformStandupType,
    transformStandupTypeForFavourites: memoizedTransformStandupTypeForFavourites,
    handleViewActionPlanDetails,
    handleOpenActionPlanDetailsById,
    handleViewActionPlan,
    fetchStandUpData,
    fetchNewsData,
    fetchCIDashboard,
    setTopOpportunities,
    setInProgressItems,
    setOverdueItems,
    setCompletedItems,
    pagination,
    currentPage,
    handlePageChange,
    handlePageSizeChange,
    submitStandUp,
    updateStandup,
    handleToggleFavoriteOpportunity,
    // Setters for parent component to update list
  };
};
